import { produce } from 'immer';
import {
  mutateInitializeResult,
  mutateUpdateTrigger,
  newTrigger,
} from '../utils/triggers';
import { fillEmptyAdminTablesBoxes } from '../utils/table';
import { mutateDeleteSection, mutateInjectSection } from '../utils/sections';
import { ItemTypes, MASKS_TYPE } from '../constants/constants';
import { gridUnit } from '../constants/gridConfig';

export const triggersActions = (set, get) => ({
  addTrigger: (parent, index = null) => {
    if (parent.type === ItemTypes.QUESTION) return;
    set(
      produce((draft) => {
        const parentId = parent.id;
        if (!draft.triggers[parentId]) {
          draft.triggers[parentId] = [];
        }

        // Déterminer le type et le nom du nouveau trigger
        let triggerType = ItemTypes.QUESTION; // Type par défaut pour le premier trigger
        let triggerId = null;
        let triggerIdFromSection = null;
        const existingTriggers = draft.triggers[parentId];

        if (existingTriggers.length > 0) {
          // Si ce n'est pas le premier trigger, copier le type et le nom du trigger au-dessus
          let referenceTrigger = null;
          if (index !== null && index >= 0 && index < existingTriggers.length) {
            // Insérer après l'index spécifié, donc copier le type et le nom du trigger à cet index
            referenceTrigger = existingTriggers[index];
          } else if (index === null) {
            // Ajout à la fin, copier le type et le nom du dernier trigger
            referenceTrigger = existingTriggers[existingTriggers.length - 1];
          }

          if (referenceTrigger) {
            triggerType = referenceTrigger.triggerType || ItemTypes.QUESTION;
            triggerId = referenceTrigger.triggerId || null;
            triggerIdFromSection =
              referenceTrigger.triggerIdFromSection || null;
          }
          // Si index < 0, on garde le type par défaut (ajout en première position)
        }

        const trigger = newTrigger({ triggerType, triggerId });
        if (triggerIdFromSection) {
          trigger.triggerIdFromSection = triggerIdFromSection;
        }
        mutateInitializeResult({ parent, trigger });

        if (index !== null) {
          draft.triggers[parentId].splice(index + 1, 0, trigger);
        } else {
          draft.triggers[parentId].push(trigger);
        }
      }),
      false,
      `add trigger to parent ${parent.id}`
    );
  },

  updateTrigger: (
    id,
    parentId,
    { type, triggerId, triggerIdFromSection, value, resultCallback }
  ) => {
    set(
      produce((draft) => {
        mutateUpdateTrigger({
          draft,
          parentId,
          id,
          type,
          triggerId,
          value,
          resultCallback,
          triggerIdFromSection,
        });
      }),
      false,
      `update trigger of id ${id} and parentId ${parentId}`
    );
  },
  reorderTriggers: (parentId, triggerIds) => {
    set(
      produce((draft) => {
        const parentTrigger = draft.triggers[parentId];
        parentTrigger.sort(
          (a, b) =>
            triggerIds.findIndex((id) => a.id === id) -
            triggerIds.findIndex((id) => b.id === id)
        );
      }),
      false,
      `Reorder triggers for parent ${parentId}`
    );
  },

  deleteTrigger: (id, parentId) => {
    set(
      produce((draft) => {
        draft.triggers[parentId] = draft.triggers[parentId].filter(
          ({ id: tId }) => tId !== id
        );
      }),
      false,
      `delete Trigger of id ${id}`
    );
  },

  deleteItemChangedByTriggers: (id) => {
    set(
      produce((draft) => {
        draft.itemsChangedByTriggers = draft.itemsChangedByTriggers.filter(
          ({ key }) => key !== id
        );
      }),
      false,
      `Delete Item Changed By Triggers of id ${id}`
    );
  },

  handleSectionsTriggersActivation: async () => {
    const sections = await Promise.all(
      get().sectionsToActivate.map(({ sectionId }) =>
        sectionId ? get().getTemplate(sectionId) : null
      )
    );
    const currentDocument = get().currentDocument;
    const sectionsContent = await Promise.all(
      sections.map(async (section) => {
        if (!section) return null;
        return {
          ...section.content,
          ...(await fillEmptyAdminTablesBoxes({
            boxes: section.content.boxes,
            containers: section.content.containers,
            columns: section.content.columns,
            proposable_id: currentDocument.id,
            type: currentDocument.type,
            landscape: get().landscape,
            marginRight: section.content.configuration.margins.right * gridUnit,
            marginLeft: section.content.configuration.margins.left * gridUnit,
            header: section.content.masks.find(
              ({ id }) => id === MASKS_TYPE.HEADER.id
            ),
            footer: section.content.masks.find(
              ({ id }) => id === MASKS_TYPE.FOOTER.id
            ),
            gridConfiguration: section.content.gridConfiguration,
            configuration: section.content.configuration,
            triggers: section.content.triggers,
            variableOrderArray: section.content.variableOrderArray,
          })),
        };
      })
    );
    set(
      produce((draft) => {
        sectionsContent.forEach((sectionContent, idx) => {
          const { id, sectionId, noId } = draft.sectionsToActivate[idx];
          mutateDeleteSection({ draft, boxId: id, optionId: noId });
          if (sectionContent)
            mutateInjectSection({
              draft,
              sectionContent,
              boxId: id,
              sectionId: sectionId,
              areItemsChangedByTriggers: true,
            });
        });
        draft.sectionsToActivate.splice(0, sectionsContent.length);
      }),
      false,
      `handleSectionsTriggersActivation}`
    );
  },
});
